// Developer: Ankit Gupta
/**
 * Colour mode.
 *
 * The palette itself lives in styles/theme.css, keyed off `data-theme` on the
 * root element. This module owns which value gets stamped there: the user's
 * choice of dark, light, or following the operating system.
 *
 * The choice is stored under its own localStorage key rather than inside the
 * main app store, because index.html reads it in a blocking script before the
 * bundle loads. Without that, every reload would paint the previous theme for
 * a frame before React corrected it.
 */
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

/** Must match the key the inline script in index.html reads. */
export const THEME_STORAGE_KEY = 'datapilot-theme';

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      // Light is what a new browser gets; dark is there for whoever turns it on.
      // 'system' is offered but is not the default: making it so would flip
      // the look with the desktop rather than with the person's own choice.
      mode: 'light',
      setMode: (mode) => set({ mode }),
    }),
    {
      name: THEME_STORAGE_KEY,
      // Version 0 stored dark for everyone, chosen or not, so it cannot tell the
      // two apart: everyone starts on light once, and dark is then a choice.
      // index.html and the Keycloak template apply the same rule before paint.
      version: 1,
      migrate: (stored, from) => (from < 1 ? { mode: 'light' } : stored) as ThemeState,
    },
  ),
);

const SYSTEM_LIGHT = '(prefers-color-scheme: light)';

function readSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia(SYSTEM_LIGHT).matches ? 'light' : 'dark';
}

/**
 * The OS setting, in state rather than read inline, so that changing it while
 * the user sits on 'system' re-renders instead of waiting for the next reload.
 */
function useSystemTheme(): ResolvedTheme {
  const [system, setSystem] = useState<ResolvedTheme>(readSystemTheme);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia(SYSTEM_LIGHT);
    const onChange = (e: MediaQueryListEvent) => setSystem(e.matches ? 'light' : 'dark');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return system;
}

/** The concrete theme to render, with 'system' resolved against the OS. */
export function useResolvedTheme(): ResolvedTheme {
  const mode = useThemeStore((s) => s.mode);
  const system = useSystemTheme();
  return mode === 'system' ? system : mode;
}

/**
 * Stamp the resolved theme on the root element, which every rule in theme.css
 * keys off, and tell the browser to match its own UI (form controls, caret,
 * scrollbars) to it.
 */
export function useApplyTheme(resolved: ResolvedTheme) {
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = resolved;
    root.style.colorScheme = resolved;
  }, [resolved]);
}

/**
 * Real colour values, for the handful of places a CSS variable cannot reach:
 * SVG presentation attributes (`fill`, `stroke`), canvas, string concatenation
 * that appends an alpha suffix, and libraries such as rc-progress that write
 * colours into an SVG attribute rather than a style rule.
 *
 * Prefer `var(--dp-*)` everywhere else. These duplicate styles/theme.css, so
 * if a value moves there, move it here too.
 */
export interface LiteralPalette {
  /** `violet` and `violet2` are the accent: the names predate the palette. */
  ink: string;
  inkDim: string;
  inkFaint: string;
  surface: string;
  surface2: string;
  line: string;
  violet: string;
  violet2: string;
  teal: string;
  magenta: string;
  success: string;
  warning: string;
  error: string;
  info: string;
}

const DARK_LITERALS: LiteralPalette = {
  ink: '#dadfe3',
  inkDim: '#c2c7cb',
  inkFaint: '#9ba1a6',
  surface: '#1f2327',
  surface2: '#2a2f33',
  line: '#2c3135',
  violet: '#dc2626',
  violet2: '#ef4444',
  teal: '#2dd4bf',
  magenta: '#d946ef',
  success: '#4ade80',
  warning: '#f59e0b',
  error: '#f87171',
  info: '#38bdf8',
};

const LIGHT_LITERALS: LiteralPalette = {
  ink: '#1c1917',
  inkDim: '#57534e',
  inkFaint: '#78716c',
  surface: '#ffffff',
  surface2: '#f5f5f4',
  line: '#e7e5e4',
  violet: '#b91c1c',
  violet2: '#dc2626',
  teal: '#0d9488',
  magenta: '#c026d3',
  success: '#15803d',
  warning: '#b45309',
  error: '#dc2626',
  info: '#0284c7',
};

export function literalPaletteFor(resolved: ResolvedTheme): LiteralPalette {
  return resolved === 'dark' ? DARK_LITERALS : LIGHT_LITERALS;
}

export function useLiteralPalette(): LiteralPalette {
  return literalPaletteFor(useResolvedTheme());
}
